import { useState } from 'react';
import { Download, File, FileText, Film, FolderPlus, Loader2, Music } from 'lucide-react';
import { api, ApiError, type ChatAttachmentRef } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { useChatAttachmentUrl } from '../hooks/useChatAttachmentUrl';
import { cn } from '@/lib/utils';
import { FileViewerModal } from './FileViewerModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  if (mimeType.startsWith('video/')) return <Film className={className} />;
  if (mimeType.startsWith('audio/')) return <Music className={className} />;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text'))
    return <FileText className={className} />;
  return <File className={className} />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AttachmentItemProps {
  attachment: ChatAttachmentRef;
  /** True when shown inside the sender's own message bubble (green tint). */
  isOwnMessage?: boolean;
  /** Channel the message belongs to — needed to resolve channel-scoped (catt_) URLs. */
  channelId: string | null;
}

export function AttachmentItem({ attachment, isOwnMessage, channelId }: AttachmentItemProps) {
  // Inline URL — renders thumbnails and previews in-page instead of downloading.
  const url = useChatAttachmentUrl(channelId, attachment.fileId, 'inline');
  const [hovered, setHovered] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [savingToVault, setSavingToVault] = useState(false);

  const isImage = attachment.mimeType.startsWith('image/');
  const isVideo = attachment.mimeType.startsWith('video/');
  // Save-to-Fias-files only applies to channel-scoped attachments; legacy
  // `vdoc_` refs already live in the Vault.
  const isChannelAttachment = attachment.fileId.startsWith('catt_');

  // Click anywhere on the attachment opens the in-page viewer (which previews
  // images/video/audio/PDF/text and offers download / save for the rest).
  const handleOpen = () => {
    if (url) setViewerOpen(true);
  };

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      // The download attribute is ignored cross-origin, so force a save via an
      // attachment-disposition presigned URL (the inline one would render).
      const dlUrl =
        isChannelAttachment && channelId
          ? (await api.getChatAttachmentDownloadUrl(channelId, attachment.fileId, 'attachment')).url
          : url;
      if (!dlUrl) return;
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = attachment.fileName;
      a.rel = 'noopener noreferrer';
      a.click();
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the file.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveToVault = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!channelId || !isChannelAttachment || savingToVault) return;
    setSavingToVault(true);
    try {
      await api.saveChatAttachmentToVault(channelId, attachment.fileId);
      toast({ description: 'Saved to your Fias files.' });
    } catch (err) {
      const description =
        err instanceof ApiError && err.code === 'STORAGE_QUOTA_EXCEEDED'
          ? 'Your Fias files storage is full.'
          : 'Could not save to your Fias files.';
      toast({ title: "Couldn't save", description, variant: 'destructive' });
    } finally {
      setSavingToVault(false);
    }
  };

  return (
    <>
      <div className="mt-1.5">
        {/* Filename label */}
        <p
          className={cn(
            // The chat bubble is always a light color (see MessageItem), so the
            // label must be dark in every theme — `text-foreground` would go
            // white in dark mode and vanish against the bubble.
            'text-[11px] mb-0.5 truncate max-w-[220px] text-black/50',
          )}
        >
          {attachment.fileName}
        </p>

        {/* Preview area */}
        <div
          className="relative inline-block cursor-pointer rounded-lg overflow-hidden"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleOpen}
          title="Click to open"
        >
          {isImage && url ? (
            <img
              src={url}
              alt={attachment.fileName}
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block"
              draggable={false}
            />
          ) : isVideo && url ? (
            /* Video element with preload=metadata shows the first frame as thumbnail */
            <video
              src={url}
              preload="metadata"
              className="max-w-[220px] max-h-[160px] object-cover rounded-lg block"
              muted
            />
          ) : (
            /* Generic file chip */
            <div
              className={cn(
                // `text-black/80` forces dark text/icon in every theme — the
                // bubble behind is always light, so inheriting `text-foreground`
                // would render white-on-light in dark mode.
                'flex items-center gap-2 px-3 py-2 rounded-lg border min-w-[160px] text-black/80',
                isOwnMessage ? 'bg-black/10 border-black/15' : 'bg-black/[0.05] border-black/10',
              )}
            >
              <FileTypeIcon
                mimeType={attachment.mimeType}
                className="h-5 w-5 flex-shrink-0 opacity-60"
              />
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate max-w-[140px]">
                  {attachment.fileName}
                </p>
                <p className="text-[11px] opacity-50">{formatBytes(attachment.sizeBytes)}</p>
              </div>
            </div>
          )}

          {/* Action overlay — appears on hover */}
          {url && hovered && (
            <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
              {isChannelAttachment && (
                <button
                  type="button"
                  onClick={handleSaveToVault}
                  disabled={savingToVault}
                  title="Save to Fias files"
                  className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors disabled:opacity-60"
                >
                  {savingToVault ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FolderPlus className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleDownload}
                title="Download"
                className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Loading shimmer while URL resolves */}
          {!url && (isImage || isVideo) && (
            <div className="w-[180px] h-[120px] rounded-lg bg-black/10 animate-pulse" />
          )}
        </div>
      </div>

      {/* Full-screen in-page viewer (images, video, audio, PDF, text + fallback) */}
      {viewerOpen && url && (
        <FileViewerModal
          attachment={attachment}
          url={url}
          onClose={() => setViewerOpen(false)}
          onDownload={handleDownload}
          onSaveToVault={isChannelAttachment ? handleSaveToVault : undefined}
          savingToVault={savingToVault}
        />
      )}
    </>
  );
}
